import { useMemo } from "react";
import {
  useGetLeadsByVendorQuery,
  useGetQuotesByAccountQuery,
  useMeQuery,
} from "generated/graphql";
import { AttendeeOption, LeadCustomer, QuoteSummary } from "./types";

export interface LeadsData {
  suggestions: AttendeeOption[];
  byEmail: Map<string, LeadCustomer>;
}

// Groups the vendor's leads by customer email: attendee suggestions (name +
// email + address) plus every quote attached to that customer.
export const useLeads = (isVendor: boolean): LeadsData => {
  const [{ data }] = useGetLeadsByVendorQuery({ pause: !isVendor });

  return useMemo(() => {
    const leads = data?.getLeadsByVendor ?? [];
    const byEmail = new Map<string, LeadCustomer>();

    leads.forEach((lead) => {
      const account = lead.chargerServiceRequest?.account;
      const email = account?.email;
      if (!email) {
        return;
      }

      const quote: QuoteSummary = {
        id: lead.id,
        estimateNumber: lead.estimateNumber,
        stage: lead.stage,
        distance: lead.distance,
        chargerModel: lead.chargerModel,
        chargerCost: lead.chargerCost,
        addOns: lead.addOns,
        installationCost: lead.installationCost,
        subtotal: lead.subtotal,
        taxLabel: lead.taxLabel,
        taxRate: lead.taxRate,
        taxAmount: lead.taxAmount,
        totalCost: lead.totalCost,
        inclusions: lead.inclusions,
        assumptions: lead.assumptions,
        notes: lead.notes,
        paymentTerms: lead.paymentTerms,
        createdAt: lead.createdAt,
      };

      const existing = byEmail.get(email);
      if (existing) {
        existing.quotes.push(quote);
      } else {
        const name =
          `${account?.firstName ?? ""} ${account?.lastName ?? ""}`.trim();
        byEmail.set(email, {
          email,
          accountId: account?.id,
          name: name || undefined,
          address: lead.chargerServiceRequest?.address ?? undefined,
          phone: account?.phoneNumber ?? undefined,
          quotes: [quote],
        });
      }
    });

    const suggestions: AttendeeOption[] = Array.from(byEmail.values()).map(
      (c) => ({ email: c.email, name: c.name, address: c.address }),
    );

    return { suggestions, byEmail };
  }, [data]);
};

// Customer side: the logged-in customer's own quotes, shaped as a LeadCustomer
// so the same QuoteDetailsDialog can render them (their "Organized by" info icon).
export const useMyQuotes = (enabled: boolean): LeadCustomer | null => {
  const [{ data }] = useGetQuotesByAccountQuery({ pause: !enabled });
  const [{ data: meData }] = useMeQuery({ pause: !enabled });

  return useMemo(() => {
    const me = meData?.me;
    if (!enabled || !me) {
      return null;
    }

    const rows = data?.getQuotesByAccount ?? [];
    const quotes: QuoteSummary[] = rows.map((q) => ({
      id: q.id,
      estimateNumber: q.estimateNumber,
      distance: q.distance,
      chargerModel: q.chargerModel,
      chargerCost: q.chargerCost,
      addOns: q.addOns,
      installationCost: q.installationCost,
      subtotal: q.subtotal,
      taxLabel: q.taxLabel,
      taxRate: q.taxRate,
      taxAmount: q.taxAmount,
      totalCost: q.totalCost,
      inclusions: q.inclusions,
      assumptions: q.assumptions,
      notes: q.notes,
      paymentTerms: q.paymentTerms,
      createdAt: q.createdAt,
    }));

    const name = `${me.firstName ?? ""} ${me.lastName ?? ""}`.trim();
    const address =
      rows.find((q) => q.chargerServiceRequest?.address)?.chargerServiceRequest
        ?.address ??
      me.address ??
      undefined;

    return {
      email: me.email,
      name: name || undefined,
      address: address ?? undefined,
      phone: me.phoneNumber ?? undefined,
      quotes,
    };
  }, [enabled, data, meData]);
};
